import React, { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { fetchMatchStats } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

// Numeric-only rows get a comparison bar
function toNumber(v) {
  if (v == null) return null;
  const n = parseFloat(String(v).replace("%", ""));
  return Number.isFinite(n) ? n : null;
}

function StatRow({ label, home, away }) {
  const hn = toNumber(home);
  const an = toNumber(away);
  const numeric = hn != null && an != null;
  const total = numeric ? Math.max(hn + an, 1) : 1;
  const homePct = numeric ? (hn / total) * 100 : 0;
  const awayPct = numeric ? (an / total) * 100 : 0;
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 items-center text-sm">
        <div className="text-left font-mono tabular-nums font-semibold">{home ?? "—"}</div>
        <div className="text-center text-[10px] uppercase tracking-[0.22em] text-white/50">
          {label}
        </div>
        <div className="text-right font-mono tabular-nums font-semibold">{away ?? "—"}</div>
      </div>
      {numeric && (
        <div className="mt-1.5 flex items-center gap-1 h-1.5">
          <div className="flex-1 bg-white/5 relative">
            <div
              className="absolute right-0 top-0 h-full bg-volt"
              style={{ width: `${homePct}%` }}
            />
          </div>
          <div className="flex-1 bg-white/5 relative">
            <div
              className="absolute left-0 top-0 h-full bg-[#38bdf8]"
              style={{ width: `${awayPct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function MatchStats({ matchId, homeTeam, awayTeam }) {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!matchId) return;
    setLoading(true);
    setError(false);
    fetchMatchStats(matchId)
      .then((d) => setStats(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [matchId]);

  if (loading) {
    return (
      <div className="border border-white/10 bg-[#121212] p-8 text-center text-white/40 text-xs uppercase tracking-[0.2em]">
        {t("stats.loading")}
      </div>
    );
  }

  if (error || !stats || !stats.has_data) {
    return (
      <div data-testid="stats-empty" className="border border-white/10 bg-[#121212] p-8 text-center text-white/40 text-xs uppercase tracking-[0.2em]">
        {t("stats.empty")}
      </div>
    );
  }

  return (
    <div data-testid="match-stats" className="border border-white/10 bg-[#121212] p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-4">
        <BarChart3 className="w-5 h-5 text-volt" />
        <h2 className="font-display text-2xl uppercase tracking-tight">{t("stats.title")}</h2>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6 pb-4 border-b border-white/5">
        <div className="text-left">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t("pred.home")}</div>
          <div className="font-display text-lg uppercase tracking-tight truncate">{homeTeam?.short || homeTeam?.name}</div>
        </div>
        <div />
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t("pred.away")}</div>
          <div className="font-display text-lg uppercase tracking-tight truncate">{awayTeam?.short || awayTeam?.name}</div>
        </div>
      </div>

      <div className="space-y-4">
        {stats.stats.map((s) => (
          <StatRow key={s.key} label={s.label} home={s.home} away={s.away} />
        ))}
      </div>

      {stats.events?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-white/5">
          <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">{t("stats.goals")}</div>
          <ul className="space-y-2 text-sm">
            {stats.events.map((ev, i) => (
              <li key={i} className="flex items-start gap-3 text-white/80">
                <span className="font-mono text-xs text-volt shrink-0 w-10">{ev.minute}</span>
                <span className="flex-1">{ev.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
